import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Button from "../common/Button";
import { AppActionCreators } from "../../store/actions/app";
import APIManager from "../../managers/APIManager";
import { capitalize } from "lodash";
import MessageText from "../common/MessageText";
import { upperCase } from "../../utils/manipulateText";
import Modal from "../common/Modal";
import { breakdowns, quickDateButtons, platforms } from "./constants";
import Form from "../common/Form";
import MultiselectBox from "../common/MultiselectBox";
import countryNames from "./countryNames";

const AppQuickFilterModal = ({ setRefreshItems }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filterName, setFilterName] = useState("");
  const selectedFilter = useSelector(
    (state) => state?.app?.selectedAppQuickFilter
  );

  const [selectedAppUser, setSelectedAppUser] = useState();
  const [selectedApps, setSelectedApps] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedDateRange, setSelectedDateRange] = useState("Y");
  const [selectedBreakdowns, setSelectedBreakdowns] = useState(["appNames"]);
  const [selectedPlatform, setSelectedPlatform] = useState("all");

  const showModal = useSelector((state) => state.app.showAppQuickFilterModal);
  const appUsers = useSelector((state) => state.site?.info?.user?.appUsers);

  const apps =
    appUsers?.find((appUser) => appUser.id === selectedAppUser?.id)?.app ?? [];

  useEffect(() => {
    if (selectedFilter?.name) {
      setFilterName(selectedFilter.name);
    } else {
      setFilterName("");
    }
    if (selectedFilter?.apps) {
      setSelectedApps(selectedFilter?.apps);
    }
    if (selectedFilter?.countries) {
      setSelectedCountries(selectedFilter?.countries);
    }
    if (selectedFilter?.dateRange) {
      setSelectedDateRange(selectedFilter?.dateRange);
    }
    if (selectedFilter?.breakdowns) {
      setSelectedBreakdowns(selectedFilter?.breakdowns);
    }
    if (selectedFilter?.appUser) {
      setSelectedAppUser(selectedFilter?.appUser);
    }
    if (selectedFilter?.platform) {
      setSelectedPlatform(selectedFilter?.platform);
    }
  }, [selectedFilter]);

  const handleClose = () => {
    dispatch(AppActionCreators.setAppQuickFilterModal(false));
    dispatch(
      AppActionCreators.setSelectedAppQuickFilter({
        mode: 0,
      })
    );
    setFilterName("");
    setError("");
  };

  const handleSave = async () => {
    if (!filterName.trim()) {
      setError(t("Please enter a filter name"));
      return;
    }

    setLoading(true);
    setError("");

    try {
      const filterData = {
        name: filterName,
        dateRange: selectedDateRange,
        breakdowns: selectedBreakdowns,
        platform: selectedPlatform,
        apps: selectedApps,
        countries: selectedCountries,
        appUser: { id: selectedAppUser?.id, name: selectedAppUser?.name },
      };

      if (selectedFilter?.mode === 0) {
        await APIManager.createAppFilter(filterData);
      } else {
        await APIManager.updateAppFilter(selectedFilter?.id, filterData);
      }

      setRefreshItems((prev) => !prev);
      handleClose();
    } catch (error) {
      setError(t("An error occurred while saving the filter"));
      console.error("Error saving filter:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isVisible={showModal}
      onClose={handleClose}
      title={upperCase(
        t(
          selectedFilter?.mode === 0 ? "create new filter" : "edit quick filter"
        )
      )}
      loading={loading}
    >
      <div className="mx-5 my-3 w-auto">
        <div className="w-full">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <div>
              <Form.Select
                className="w-full col-span-1"
                name="App User"
                label={capitalize(t("app user"))}
                value={selectedAppUser?.id || "all-apps"}
                onChange={(e) => {
                  const appUser = appUsers.find(
                    (app) => app.id === e.target.value
                  );
                  setSelectedApps([]);
                  setSelectedAppUser(appUser);
                }}
              >
                <option value="all-apps">{t("All Apps")}</option>
                {appUsers.map((app) => (
                  <option key={app.id} value={app.id}>
                    {app.name}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-span-2">
              <Form.FormTextInput
                name="filter-name"
                label={capitalize(t("filter name"))}
                placeholder={capitalize(t("filter name"))}
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
              />
              {error && (
                <MessageText
                  message={error}
                  textAlignment="text-left"
                  customClass="text-red-500 mt-1"
                />
              )}
            </div>
            <div>
              <Form.Select
                className="w-full col-span-1"
                name="Date Range"
                label={capitalize(t("date range"))}
                value={selectedDateRange || "Y"}
                onChange={(e) => setSelectedDateRange(e.target.value)}
              >
                {quickDateButtons.map((button) => (
                  <option key={button.range} value={button.range}>
                    {capitalize(t(button.text))}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>

          <div className="flex flex-row gap-2">
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {capitalize(t("breakdowns"))}
              </label>
              <span className="md:inline-block bg-gray-200 text-gray-700 border border-gray-200 rounded py-2 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500">
                {Object.entries(breakdowns).map(([key, value]) => (
                  <button
                    key={key}
                    className={`transition-all ease-out transition-fast text-xs font-bold px-2 py-2 rounded mr-2 cursor-pointer ${
                      selectedBreakdowns?.includes(key) ? "bg-white shadow" : ""
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      const newBreakdowns = selectedBreakdowns?.includes(key)
                        ? selectedBreakdowns.filter((b) => b !== key)
                        : [...(selectedBreakdowns || []), key];
                      setSelectedBreakdowns(newBreakdowns);
                    }}
                  >
                    {upperCase(t(value))}
                  </button>
                ))}
              </span>
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {capitalize(t("platforms"))}
              </label>
              <span className="md:inline-block bg-gray-200 text-gray-700 border border-gray-200 rounded py-2 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500">
                {platforms.map((platform) => (
                  <button
                    key={platform}
                    className={`transition-all ease-out transition-fast text-xs font-bold px-2 py-2 rounded mr-2 cursor-pointer ${
                      selectedPlatform === platform ? "bg-white shadow" : ""
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedPlatform(platform);
                    }}
                  >
                    {upperCase(t(platform))}
                  </button>
                ))}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="mt-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {capitalize(t("apps"))}
              </label>
              <div>
                <MultiselectBox
                  title={capitalize(t("apps"))}
                  options={apps?.map((app) => ({
                    id: app,
                    label: app,
                  }))}
                  onChange={setSelectedApps}
                  onConfig
                  fullWidth
                  customStyle={{
                    maxHeight: "300px",
                    maxWidth: "400px",
                  }}
                  customClass="overflow-x-scroll"
                  selectedItems={selectedApps ?? []}
                  defaultSelectedItems={selectedApps ?? []}
                  filterPlaceholderText={"appFilterText"}
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {capitalize(t("countries"))}
              </label>
              <div>
                <MultiselectBox
                  title={capitalize(t("countries"))}
                  options={Object.entries(countryNames).map(([name, code]) => ({
                    id: code,
                    label: name,
                  }))}
                  onChange={setSelectedCountries}
                  onConfig
                  fullWidth
                  customStyle={{
                    maxHeight: "300px",
                    maxWidth: "400px",
                  }}
                  customClass="overflow-x-scroll"
                  defaultSelectedItems={selectedCountries ?? []}
                  filterPlaceholderText={"countryFilterText"}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-row justify-end w-full mt-2">
          <Button
            className="px-4 mr-2 whitespace-nowrap overflow-hidden"
            color="login"
            type="button"
            onClick={handleSave}
            loading={loading}
          >
            {upperCase(t("save"))}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AppQuickFilterModal;
